import type { CoachingPartnership } from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import type { CoachProfile } from '@efdigital/ll-addon-data/types/coaching/coach-profile';
import { cacheKeyFor, type Store } from '@warp-drive/core';
import {
  createRecord,
  deleteRecord,
  query,
  updateRecord,
} from '@warp-drive/utilities/json-api';

import { filterOr, jsonApiOptions, page } from '../../json-api.ts';

// Picklist group keys used by coach profile fields, for the editor's pickers.
export const COACHING_SPECIALISMS_GROUP = 'coaching-specialisms';
export const COACHING_COUNTRIES_GROUP = 'coaching-countries';
export const COACHING_TYPES_GROUP = 'coaching-types';
export const COACHING_INDUSTRIES_GROUP = 'coaching-industries';
export const COACHING_SENIORITY_GROUP = 'coaching-seniority';

export function queryCoachProfileByUserId(
  userId: string,
  status: CoachProfile['status']
) {
  return query<CoachProfile>(
    DataTypeCoaching.CoachProfile,
    {
      'filter[user]': userId,
      'filter[status]': status,
      ...page({ offset: 0, limit: 1 }),
      'sort[]': '-modified',
    },
    jsonApiOptions
  );
}

export function queryCoachProfilesByUserIds(userIds: string[]) {
  return query<CoachProfile>(
    DataTypeCoaching.CoachProfile,
    {
      ...filterOr('filter', '[user]', userIds),
      'filter[status]': 'published',
      ...page({ offset: 0, limit: userIds.length }),
    },
    jsonApiOptions
  );
}

export function queryPublishedCoachProfiles() {
  return query<CoachProfile>(
    DataTypeCoaching.CoachProfile,
    {
      'filter[status]': 'published',
      ...page({ offset: 0, limit: 500 }),
      'sort[]': '-modified',
    },
    jsonApiOptions
  );
}

export function queryCoachProfilesByPartnerships(
  partnerships: CoachingPartnership[]
) {
  return queryCoachProfilesByUserIds(
    partnerships.map((partnership) => partnership.coachUserId)
  );
}

// The whole cached resource (fragment arrays included as attributes) is sent
// as the JSON:API body, matching the coaching-partnerships builder pattern —
// no separate deep-save is needed for the profile's fragment arrays.
export function createCoachProfile(coachProfile: CoachProfile, store: Store) {
  const init = createRecord(coachProfile, { ...jsonApiOptions });

  init.body = JSON.stringify({
    data: store.cache.peek(cacheKeyFor(coachProfile)),
  });

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function updateCoachProfile(coachProfile: CoachProfile, store: Store) {
  const init = updateRecord(coachProfile, { ...jsonApiOptions, patch: true });

  init.body = JSON.stringify({
    data: store.cache.peek(cacheKeyFor(coachProfile)),
  });

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function deleteCoachProfile(coachProfile: CoachProfile) {
  return deleteRecord(coachProfile, jsonApiOptions);
}
